import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { MapContainer, Marker, Polyline, Popup, TileLayer, Tooltip as LeafletTooltip, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import useDateTimeFormat from '../../../hooks/useDateTimeFormat';
import { Visit } from '../../../types/Visit';

interface StationToStationMapProps {
  deviceId: string;
  visits: Visit[];
  activeVisit?: Visit | null;
  activeVisitId?: string;
  onStationClick?: (visitId: string) => void;
  onActiveStationClick?: () => void;
}

interface StationPoint {
  id: string;
  visitId?: string;
  stationCode: string;
  latitude: number;
  longitude: number;
  firstSeenDateTime?: Date;
  lastSeenDateTime?: Date;
  isActive?: boolean;
}

interface CurvedSegment {
  key: string;
  points: [number, number][];
}

const MAP_HEIGHT = 620;

// Curvature tuning: BASE is how curved a single (non-repeated) hop looks;
// STEP is how much extra bend each additional repeat of the same pair gets.
const BASE_CURVATURE = 0.18;
const CURVE_STEP = 0.18;
const CURVE_SAMPLES = 32;

const InvalidateMapSize: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    requestAnimationFrame(() => map.invalidateSize());
  }, [map]);

  return null;
};

const FitBounds: React.FC<{ coords: [number, number][] }> = ({ coords }) => {
  const map = useMap();

  useEffect(() => {
    if (!coords.length) return;
    const bounds = L.latLngBounds(coords.map(([lat, lng]) => L.latLng(lat, lng)));
    map.fitBounds(bounds, { padding: [60, 60], maxZoom: 10 });
  }, [coords, map]);

  return null;
};

const ResetView: React.FC<{ coords: [number, number][] }> = ({ coords }) => {
  const map = useMap();
  const handleReset = () => {
    if (coords.length > 0) {
      const bounds = L.latLngBounds(coords.map(([lat, lng]) => L.latLng(lat, lng)));
      map.fitBounds(bounds, { padding: [60, 60], maxZoom: 10, animate: true });
    }
  };
  return (
    <div className='leaflet-top leaflet-left' style={{ marginTop: 80, pointerEvents: 'auto' }}>
      <div className='leaflet-control leaflet-bar'>
        <a
          href='#'
          title='Reset to full view'
          role='button'
          aria-label='Reset to full view'
          onClick={(e) => {
            e.preventDefault();
            handleReset();
          }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, color: '#444', textDecoration: 'none' }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='14'
            height='14'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <path d='M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8' />
            <path d='M3 3v5h5' />
          </svg>
        </a>
      </div>
    </div>
  );
};

const makeStationIcon = (stationCode: string) =>
  L.divIcon({
    className: '',
    html: `<div style="
      min-width:34px;
      height:24px;
      padding:0 8px;
      border-radius:999px;
      background:#7c3aed;
      color:#ffffff;
      display:flex;
      align-items:center;
      justify-content:center;
      font-size:11px;
      font-weight:700;
      line-height:1;
      letter-spacing:0.25px;
      box-shadow:0 2px 8px rgba(76, 29, 149, 0.35);
      border:1px solid rgba(255,255,255,0.65);
    ">${stationCode}</div>`,
    iconSize: [34, 24],
    iconAnchor: [17, 12],
    popupAnchor: [0, -12],
  });

const makeActiveStationIcon = (stationCode: string) =>
  L.divIcon({
    className: '',
    html: `<div style="
      min-width:72px;
      height:24px;
      padding:0 10px;
      border-radius:999px;
      background:#16a34a;
      color:#ffffff;
      display:flex;
      align-items:center;
      justify-content:center;
      font-size:11px;
      font-weight:700;
      line-height:1;
      letter-spacing:0.5px;
      box-shadow:0 2px 8px rgba(22, 163, 74, 0.4);
      border:1px solid rgba(255,255,255,0.8);
      cursor:pointer;
      white-space:nowrap;
    ">ACTIVE - ${stationCode}</div>`,
    iconSize: [90, 24],
    iconAnchor: [45, 12],
    popupAnchor: [0, -12],
  });

const makeArrowIcon = (angle: number) =>
  L.divIcon({
    className: '',
    html: `<div style="
      width:18px;
      height:18px;
      display:flex;
      align-items:center;
      justify-content:center;
      transform: rotate(${angle}deg);
      filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3));
    ">
      <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 2L15 15H3L9 2Z" fill="#5b21b6" stroke="#ffffff" stroke-width="1.2" stroke-linejoin="round"/>
      </svg>
    </div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });

const getProjectedArrowAngle = (map: L.Map, from: [number, number], to: [number, number]): number => {
  const fromPoint = map.latLngToLayerPoint(L.latLng(from[0], from[1]));
  const toPoint = map.latLngToLayerPoint(L.latLng(to[0], to[1]));
  const dx = toPoint.x - fromPoint.x;
  const dy = toPoint.y - fromPoint.y;
  return (Math.atan2(dy, dx) * 180) / Math.PI + 270;
};

// Samples a quadratic Bezier curve between `from` and `to`. `curvature` controls
// how far the control point is pushed off the straight line, as a fraction of
// the segment length, and its sign controls which side it bulges toward.
const quadraticBezierPoints = (from: [number, number], to: [number, number], curvature: number, steps = CURVE_SAMPLES): [number, number][] => {
  const [lat1, lng1] = from;
  const [lat2, lng2] = to;

  const dLat = lat2 - lat1;
  const dLng = lng2 - lng1;
  const dist = Math.sqrt(dLat * dLat + dLng * dLng);

  if (dist === 0) return [from, to];

  const midLat = (lat1 + lat2) / 2;
  const midLng = (lng1 + lng2) / 2;

  // Unit vector perpendicular to the from->to line
  const perpLat = -dLng / dist;
  const perpLng = dLat / dist;

  const controlLat = midLat + perpLat * dist * curvature;
  const controlLng = midLng + perpLng * dist * curvature;

  const pts: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const oneMinusT = 1 - t;
    const lat = oneMinusT * oneMinusT * lat1 + 2 * oneMinusT * t * controlLat + t * t * lat2;
    const lng = oneMinusT * oneMinusT * lng1 + 2 * oneMinusT * t * controlLng + t * t * lng2;
    pts.push([lat, lng]);
  }
  return pts;
};

// Builds one curved segment per hop. Repeated hops between the same two
// stations (in either direction) get progressively larger, alternating-side
// curvature so they fan out instead of overlapping.
const buildCurvedSegments = (stationPoints: StationPoint[]): CurvedSegment[] => {
  const occurrenceByPair = new Map<string, number>();
  const segments: CurvedSegment[] = [];

  for (let i = 0; i < stationPoints.length - 1; i++) {
    const from = stationPoints[i];
    const to = stationPoints[i + 1];

    if (from.isActive || to.isActive) continue;

    const samePoint = Math.abs(from.latitude - to.latitude) < 0.000001 && Math.abs(from.longitude - to.longitude) < 0.000001;
    if (samePoint) continue;

    const pairKey = [from.stationCode, to.stationCode].sort().join('|');
    const occurrence = occurrenceByPair.get(pairKey) ?? 0;
    occurrenceByPair.set(pairKey, occurrence + 1);

    const side = occurrence % 2 === 0 ? 1 : -1;
    const magnitude = BASE_CURVATURE + Math.floor(occurrence / 2) * CURVE_STEP;
    const curvature = side * magnitude;

    segments.push({
      key: `seg-${i}-${from.id}-${to.id}`,
      points: quadraticBezierPoints([from.latitude, from.longitude], [to.latitude, to.longitude], curvature),
    });
  }

  return segments;
};

const RouteArrows: React.FC<{ segments: CurvedSegment[] }> = ({ segments }) => {
  const map = useMap();
  const [mapVersion, setMapVersion] = useState(0);

  useMapEvents({
    zoomend: () => setMapVersion((version) => version + 1),
    moveend: () => setMapVersion((version) => version + 1),
  });

  const arrows = useMemo(() => {
    return segments
      .map((seg) => {
        const pts = seg.points;
        if (pts.length < 2) return null;

        const midIndex = Math.floor(pts.length / 2);
        const a = pts[Math.max(0, midIndex - 1)];
        const b = pts[Math.min(pts.length - 1, midIndex + 1)];
        const mid = pts[midIndex];

        const angle = getProjectedArrowAngle(map, a, b);

        return { key: `arrow-${seg.key}`, mid, angle };
      })
      .filter((a): a is { key: string; mid: [number, number]; angle: number } => a !== null);
    // mapVersion forces recompute on pan/zoom so angles stay visually correct under the current projection
  }, [segments, map, mapVersion]);

  return (
    <>
      {arrows.map((arrow) => (
        <Marker key={arrow.key} position={arrow.mid} icon={makeArrowIcon(arrow.angle)} interactive={false} />
      ))}
    </>
  );
};

const toTimestamp = (value?: Date | string | null): number => {
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const resolveVisitLocation = (visit: Visit): { latitude: number; longitude: number } | null => {
  if (visit.location?.latitude != null && visit.location?.longitude != null) {
    return visit.location;
  }

  const readers = Object.values(visit.readers ?? {});
  const latestReader = readers
    .filter((reader) => reader.location?.latitude != null || reader.lockedLocation?.latitude != null)
    .sort((a, b) => {
      const aTime = Math.max(toTimestamp(a.lastSeenDateTime), toTimestamp(a.lastSighting), toTimestamp(a.lastTimeOfReport));
      const bTime = Math.max(toTimestamp(b.lastSeenDateTime), toTimestamp(b.lastSighting), toTimestamp(b.lastTimeOfReport));
      return bTime - aTime;
    })[0];

  return latestReader?.location ?? latestReader?.lockedLocation ?? null;
};

const uniqueStationPath = (rows: Visit[], activeVisit?: Visit | null, activeVisitId?: string): StationPoint[] => {
  const mapped: StationPoint[] = rows.flatMap((r): StationPoint[] => {
    const location = resolveVisitLocation(r);
    if (!location) return [];

    return [
      {
        id: r.id,
        visitId: r.visitId,
        stationCode: (r.stationIataCode || '---').toUpperCase(),
        latitude: location.latitude,
        longitude: location.longitude,
        firstSeenDateTime: r.firstSeenDateTime,
        lastSeenDateTime: r.lastSeenDateTime,
        isActive: false,
      },
    ];
  });

  const targetActiveId = activeVisit?.visitId || activeVisitId;
  let lastActiveIndex = -1;

  if (targetActiveId) {
    for (let i = mapped.length - 1; i >= 0; i--) {
      if (mapped[i].visitId === targetActiveId) {
        lastActiveIndex = i;
        break;
      }
    }
  }

  if (lastActiveIndex === -1 && !targetActiveId && rows.length > 0) {
    for (let i = mapped.length - 1; i >= 0; i--) {
      const row = rows.find((visit) => visit.id === mapped[i].id);
      if (row?.isActive || row?.status?.toLowerCase() === 'active') {
        lastActiveIndex = i;
        break;
      }
    }
  }

  if (lastActiveIndex !== -1) {
    const activeLocation = activeVisit ? resolveVisitLocation(activeVisit) : null;
    if (activeVisit && activeLocation) {
      mapped[lastActiveIndex].latitude = activeLocation.latitude;
      mapped[lastActiveIndex].longitude = activeLocation.longitude;
      if (activeVisit.stationIataCode) {
        mapped[lastActiveIndex].stationCode = activeVisit.stationIataCode.toUpperCase();
      }
      if (activeVisit.firstSeenDateTime) mapped[lastActiveIndex].firstSeenDateTime = activeVisit.firstSeenDateTime;
      if (activeVisit.lastSeenDateTime) mapped[lastActiveIndex].lastSeenDateTime = activeVisit.lastSeenDateTime;
    }
    mapped[lastActiveIndex].isActive = true;
  } else if (activeVisit) {
    const activeLocation = resolveVisitLocation(activeVisit);
    if (!activeLocation) {
      return mapped;
    }

    mapped.push({
      id: activeVisit.id || activeVisit.visitId || 'active-visit',
      visitId: activeVisit.visitId,
      stationCode: (activeVisit.stationIataCode || '---').toUpperCase(),
      latitude: activeLocation.latitude,
      longitude: activeLocation.longitude,
      firstSeenDateTime: activeVisit.firstSeenDateTime,
      lastSeenDateTime: activeVisit.lastSeenDateTime,
      isActive: true,
    });
  }

  const reduced = mapped.reduce<StationPoint[]>((acc, point) => {
    const prev = acc[acc.length - 1];
    if (!prev) {
      acc.push(point);
      return acc;
    }

    const sameStation = prev.stationCode === point.stationCode;
    const sameLat = Math.abs(prev.latitude - point.latitude) < 0.000001;
    const sameLng = Math.abs(prev.longitude - point.longitude) < 0.000001;

    if (sameStation && sameLat && sameLng) {
      if (point.isActive) {
        prev.isActive = true;
        if (point.visitId) prev.visitId = point.visitId;
      }
      return acc;
    }

    acc.push(point);
    return acc;
  }, []);

  let foundActive = false;
  for (let i = reduced.length - 1; i >= 0; i--) {
    if (reduced[i].isActive && !foundActive) {
      foundActive = true;
    } else if (foundActive) {
      reduced[i].isActive = false;
    }
  }

  return reduced;
};

export default function StationToStationMap({
  deviceId,
  visits,
  activeVisit,
  activeVisitId,
  onStationClick,
  onActiveStationClick,
}: StationToStationMapProps) {
  const { toStandardDate, withTimeMode } = useDateTimeFormat();
  // VisitHistory is the single source of truth for the station route.
  // The map receives exactly the same rows that are currently shown by the table.
  const points = useMemo(() => {
    if (!deviceId) return [];

    const rows = [...visits].sort((a, b) => {
      const aTime = toTimestamp(a.firstSeenDateTime);
      const bTime = toTimestamp(b.firstSeenDateTime);
      return aTime - bTime;
    });

    const targetActiveId = activeVisitId ?? activeVisit?.visitId;
    const activeRow = targetActiveId ? rows.find((visit) => visit.visitId === targetActiveId) : undefined;

    return uniqueStationPath(rows, activeRow ?? activeVisit ?? null, targetActiveId);
  }, [deviceId, visits, activeVisit, activeVisitId]);

  const coords = useMemo<[number, number][]>(() => points.map((p) => [p.latitude, p.longitude]), [points]);
  const curvedSegments = useMemo(() => buildCurvedSegments(points), [points]);

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mt: 1, height: MAP_HEIGHT }}>
      <Box sx={{ height: '100%', position: 'relative' }}>
        {!coords.length ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant='body2' color='text.secondary'>
              No station route data found.
            </Typography>
          </Box>
        ) : (
          <MapContainer
            center={L.latLng(coords[0][0], coords[0][1])}
            zoom={5}
            scrollWheelZoom
            zoomControl
            attributionControl
            style={{ height: '100%', width: '100%' }}
          >
            <InvalidateMapSize />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
            />

            <FitBounds coords={coords} />
            <ResetView coords={coords} />

            {curvedSegments.map((seg) => (
              <Polyline key={seg.key} positions={seg.points} pathOptions={{ color: '#7c3aed', weight: 3, opacity: 0.9 }} />
            ))}

            <RouteArrows segments={curvedSegments} />

            {points.map((point, index) => (
              <Marker
                key={`${point.id}-${index}`}
                position={[point.latitude, point.longitude]}
                icon={point.isActive ? makeActiveStationIcon(point.stationCode) : makeStationIcon(point.stationCode)}
                eventHandlers={{
                  click: () => {
                    if (point.isActive && onActiveStationClick) {
                      onActiveStationClick();
                    } else if (point.visitId) {
                      onStationClick?.(point.visitId);
                    }
                  },
                }}
              >
                <LeafletTooltip direction='top' offset={[0, -12]}>
                  <Typography variant='caption' fontWeight={700}>
                    {point.stationCode} {point.isActive ? '(Active)' : ''}
                  </Typography>
                </LeafletTooltip>
                <Popup>
                  <Typography variant='caption' fontWeight={700} display='block' sx={{ color: point.isActive ? '#15803d' : 'inherit' }}>
                    Station {point.stationCode} {point.isActive ? '(Active Visit)' : ''}
                  </Typography>
                  <Typography variant='caption' color='text.secondary' display='block'>
                    Step {index + 1}
                  </Typography>
                  {point.firstSeenDateTime && (
                    <Typography variant='caption' display='block'>
                      {withTimeMode('First')}: {toStandardDate(point.firstSeenDateTime)}
                    </Typography>
                  )}
                  {point.lastSeenDateTime && (
                    <Typography variant='caption' display='block'>
                      {withTimeMode('Last')}: {toStandardDate(point.lastSeenDateTime)}
                    </Typography>
                  )}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </Box>
    </Paper>
  );
}
